import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button } from 'antd';
import { logger } from './services/logger.client';
import { captureException } from './services/sentry.client';
import { ApiError } from './services/apiErrors';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    logger.error({ err: error, componentStack: errorInfo.componentStack }, "Uncaught error in component tree");
    captureException(error, { componentStack: errorInfo.componentStack });
  }

  public render() {
    if (this.state.hasError) {
      let subTitle = 'Sorry, something went wrong. Please try refreshing the page.';
      if (this.state.error instanceof ApiError) {
        subTitle = `API Error (${this.state.error.status}): ${this.state.error.message}`;
      } else if (this.state.error) {
        subTitle = this.state.error.message;
      }

      return (
        <Result
          status="error"
          title="Application Error"
          subTitle={subTitle}
          extra={<Button type="primary" onClick={() => window.location.reload()}>Refresh Page</Button>}
        />
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;